import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

const MAX_NAME_LENGTH = 100
const MAX_EMAIL_LENGTH = 256
const MIN_PASSWORD_LENGTH = 6

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const passwordHasDigitRegex = /\d/

export default function Register() {
  const { register } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnUrl = useMemo(() => searchParams.get('returnUrl') || '/', [searchParams])

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const updateField = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
    setError('')
  }

  const validate = () => {
    const firstName = form.firstName.trim()
    const lastName = form.lastName.trim()
    const email = form.email.trim()

    if (!firstName) return 'El nombre es obligatorio.'
    if (firstName.length > MAX_NAME_LENGTH) return 'El nombre no puede superar 100 caracteres.'
    if (!lastName) return 'El apellido es obligatorio.'
    if (lastName.length > MAX_NAME_LENGTH) return 'El apellido no puede superar 100 caracteres.'
    if (!email) return 'El correo electrónico es obligatorio.'
    if (email.length > MAX_EMAIL_LENGTH) return 'El correo electrónico no puede superar 256 caracteres.'
    if (!emailRegex.test(email)) return 'Ingresa un correo electrónico válido.'
    if (!form.password) return 'La contraseña es obligatoria.'
    if (form.password.length < MIN_PASSWORD_LENGTH) return 'La contraseña debe tener al menos 6 caracteres.'
    if (!passwordHasDigitRegex.test(form.password)) return 'La contraseña debe incluir al menos un número.'
    if (!form.confirmPassword) return 'Debes confirmar la contraseña.'
    if (form.password !== form.confirmPassword) return 'Las contraseñas no coinciden.'

    return ''
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password
      })

      if (result?.requiresEmailConfirmation) {
        navigate(`/login?verificationPending=1&email=${encodeURIComponent(form.email.trim())}`)
        return
      }

      if (returnUrl && returnUrl !== '/') {
        window.location.href = returnUrl
      } else {
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err.message || 'No fue posible crear la cuenta.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-primary-600">AppEmprendedor</h1>
          <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
            Crear cuenta
          </h2>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <form className="mt-8 space-y-4" onSubmit={handleSubmit} noValidate>
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              Nombre
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              maxLength={MAX_NAME_LENGTH}
              required
              value={form.firstName}
              onChange={updateField}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Tu nombre"
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Apellido
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              maxLength={MAX_NAME_LENGTH}
              required
              value={form.lastName}
              onChange={updateField}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Tu apellido"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Correo electrónico
            </label>
            <input
              id="email"
              name="email"
              type="email"
              maxLength={MAX_EMAIL_LENGTH}
              autoComplete="email"
              required
              value={form.email}
              onChange={updateField}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="correo@ejemplo.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Contraseña
            </label>
            <input
              id="password"
              name="password"
              type="password"
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              required
              value={form.password}
              onChange={updateField}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Mínimo 6 caracteres y un número"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirmar contraseña
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={form.confirmPassword}
              onChange={updateField}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Repite la contraseña"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 rounded-md text-white bg-primary-600 hover:bg-primary-700 text-sm disabled:opacity-50"
          >
            {loading ? 'Creando cuenta...' : 'Crear cuenta'}
          </button>
        </form>

        <div className="text-center">
          <Link className="text-sm text-primary-600 hover:text-primary-700" to="/login">
            Ya tengo cuenta, iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  )
}



